<script setup lang="ts">
  import throttle from 'lodash-es/throttle';
  import { computed, inject, ref } from 'vue';

  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import { DATA_VIEW_INJECTION } from '../DataView/DataView.vue';
  import { DATA_VIEW_FILTERS_UTILS_INJECTION } from '../DataViewFilters/DataViewFilters.keys';
  import type { OnApplyFilters } from '../DataViewFilters/DataViewFilters.types';
  import Dropdown from '../Dropdown/Dropdown.vue';
  import FilterChip from '../FilterChip/FilterChip.vue';

  export interface FilterDropdownProps {
    label: string;
    /** The name of a filter group */
    group: string;
    onApply?: OnApplyFilters;
  }
  const props = withDefaults(defineProps<FilterDropdownProps>(), {
    activeFilterCount: 0,
    onApply: () => undefined,
  });

  const emit = defineEmits<{
    (e: 'reset'): void;
    (e: 'dismiss'): void;
  }>();

  const { isLoading: isDataViewLoading } = inject(DATA_VIEW_INJECTION.key, DATA_VIEW_INJECTION.defaults);

  const dataViewFiltersUtils = inject(DATA_VIEW_FILTERS_UTILS_INJECTION.key);

  if (!dataViewFiltersUtils?.useFiltersInstance) {
    throw new Error(
      'FilterDropdown must be used within a <DataViewFilters> that receives an instance of useFilters().',
    );
  }

  const { applyFilters, resetFilterGroup, activeFiltersCounts, undoWorkingFilters } =
    dataViewFiltersUtils.useFiltersInstance;

  const dropdownRef = ref<InstanceType<typeof Dropdown>>();
  const isOpen = computed(() => dropdownRef.value?.isActive);

  function onToggleButtonClick() {
    const wasOpen = isOpen.value;

    dropdownRef.value?.toggle();

    if (wasOpen) {
      undoWorkingFilters();
      emit('dismiss');
    }
  }

  function handleResetClick() {
    resetFilterGroup(props.group);
    emit('reset');
    dropdownRef.value?.dismiss();
  }

  async function handleApplyClick() {
    const { preventDismiss } = (await props.onApply?.()) || applyFilters() || {};

    if (!preventDismiss) {
      dropdownRef.value?.dismiss();
    }
  }

  function onDismiss() {
    undoWorkingFilters();
    emit('dismiss');
  }

  const showShadow = ref(false);
  const onScroll = throttle((event: Event) => {
    if (!event.target) return;

    if ((event.target as HTMLElement).scrollTop > 0) {
      showShadow.value = true;
    } else {
      showShadow.value = false;
    }
  }, 500);

  function onScrollEnd() {
    setTimeout(() => {
      showShadow.value = false;
    }, 2000);
  }
</script>

<template>
  <Dropdown
    ref="dropdownRef"
    align="left"
    class="stash-filter-dropdown"
    data-test="stash-filter-dropdown"
    fluid-content
    close-manually
    content-class="w-full max-w-[600px] h-[400px]"
    :offset="{ y: 6 }"
    @dismiss="onDismiss"
  >
    <template #toggle>
      <FilterChip
        class="rounded-full"
        has-dropdown
        :is-dropdown-open="isOpen"
        :filter-count="activeFiltersCounts[props.group]"
        :is-selected="isOpen"
        @click="onToggleButtonClick"
      >
        {{ props.label }}
      </FilterChip>
    </template>
    <template #default>
      <div class="flex h-full flex-col rounded">
        <div class="flex-1 overflow-hidden rounded-t">
          <div class="relative h-full overflow-auto p-6" @scroll="onScroll" @scrollend="onScrollEnd">
            <slot></slot>
          </div>
          <Transition name="fade">
            <div
              v-if="showShadow"
              class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[20px] bg-scroll-shadow"
            ></div>
          </Transition>
        </div>

        <footer
          class="flex shrink-0 justify-end gap-6 rounded-b border border-solid border-x-ice-100 border-b-ice-100 border-t-ice-200 bg-ice-100 p-3.5"
        >
          <Button
            v-if="activeFiltersCounts[props.group]"
            secondary
            class="min-w-[100px]"
            :disabled="isDataViewLoading"
            @click="handleResetClick"
          >
            {{ t('ll.reset') }}
          </Button>
          <Button class="min-w-[100px]" :disabled="isDataViewLoading" @click="handleApplyClick">
            {{ t('ll.apply') }}
          </Button>
        </footer>
      </div>
    </template>
  </Dropdown>
</template>
